import { Fragment, useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';

import { subDays, format as formatDate } from 'date-fns';

import { DashboardContent } from 'src/layouts/dashboard';

import { Iconify } from 'src/components/iconify';
import { ReportScopeFilter } from 'src/components/report-filter/ReportScopeFilter';
import { useReportScope } from 'src/routes/context/ReportScopeContext';

import { downloadBlob } from 'src/utils/download-file';

import { severity, SCORE_FORMULA_TR, type SevColor } from 'src/sections/reports/utils/score-severity';

import {
  getRegularity,
  exportRegularity,
  type SignalType,
  type ExportFormat,
  type RegularityMode,
  type RegularityReport,
  type EmployeeRegularity,
} from 'src/api/reports.api';

const SIGNAL_LABEL: Record<SignalType, { label: string; color: 'warning' | 'info' | 'error' | 'default' }> = {
  LATE: { label: 'Geç giriş', color: 'warning' },
  EARLY_LEAVE: { label: 'Erken çıkış', color: 'info' },
  ABSENCE: { label: 'Devamsızlık', color: 'error' },
  FORGOTTEN_CHECKOUT: { label: 'Unutulan çıkış', color: 'default' },
};

function isoDaysAgo(days: number): string {
  // FE-5: yerel takvim günü — toISOString() UTC'ye çevirip günü kaydırıyordu (UTC+3 off-by-one).
  return formatDate(subDays(new Date(), days), 'yyyy-MM-dd');
}

function EventDetail({ emp }: { emp: EmployeeRegularity }) {
  if (!emp.events.length) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
        Bu dönemde ihlal kaydı yok.
      </Typography>
    );
  }
  return (
    <Box sx={{ p: 2, bgcolor: 'background.neutral' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Gün-gün ihlal dökümü
      </Typography>
      <Stack spacing={1}>
        {emp.events.map((ev, i) => {
          const meta = SIGNAL_LABEL[ev.type];
          return (
            <Stack key={`${ev.date}-${i}`} direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 96, fontWeight: 500 }}>
                {ev.date}
              </Typography>
              <Chip size="small" color={meta.color} label={meta.label} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {ev.checkIn ? `Giriş ${ev.checkIn.slice(0, 5)}` : ''}
                {ev.checkOut ? ` · Çıkış ${ev.checkOut.slice(0, 5)}` : ''}
                {ev.deviationMinutes != null ? ` · ${ev.deviationMinutes} dk sapma` : ''}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

function RegularityInner({ mode }: { mode: RegularityMode }) {
  const { companyId, branchId } = useReportScope();
  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(isoDaysAgo(0));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RegularityReport | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState<ExportFormat | null>(null);

  const isIrregular = mode === 'irregular';
  const title = isIrregular ? 'En Düzensiz Çalışanlar' : 'En Düzenli Çalışanlar';

  // FE-4: ters/boş tarih aralığı guard'ı
  const rangeInvalid = !start || !end || new Date(start) > new Date(end);

  const handleFetch = async () => {
    setError(null);
    if (rangeInvalid) {
      setError('Başlangıç tarihi bitişten sonra olamaz.');
      return;
    }
    setLoading(true);
    try {
      const res = await getRegularity(mode, { companyId, branchId, start, end });
      setData(res);
      setExpanded(new Set());
    } catch (e) {
      setError('Rapor yüklenemedi. Yetki veya bağlantı sorununu kontrol edin.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: ExportFormat) => {
    setExporting(format);
    try {
      const blob = await exportRegularity(mode, { companyId, branchId, start, end }, format);
      downloadBlob(blob, `${mode}-calisanlar-${start}_${end}`, format);
    } catch (e) {
      setError('Dışa aktarma başarısız oldu.');
    } finally {
      setExporting(null);
    }
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rows = data?.overall ?? [];

  // Skor dağılımı (risk bantları) + ortalama — kullanıcı skoru yorumlayabilsin diye.
  const dist = { Yüksek: 0, Orta: 0, Düşük: 0, Sorunsuz: 0 } as Record<string, number>;
  rows.forEach((r) => {
    dist[severity(r.score, r.expectedWorkdays).label] += 1;
  });
  const avgScore = rows.length
    ? Math.round((rows.reduce((s, r) => s + r.score, 0) / rows.length) * 10) / 10
    : 0;
  const DIST_META: { key: string; color: SevColor }[] = [
    { key: 'Yüksek', color: 'error' },
    { key: 'Orta', color: 'warning' },
    { key: 'Düşük', color: 'info' },
    { key: 'Sorunsuz', color: 'success' },
  ];

  return (
    <DashboardContent>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4">{title}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Geç giriş, erken çıkış, devamsızlık ve unutulan çıkış sinyallerinden hesaplanan skora göre
          sıralama. Satıra tıklayarak gün-gün dökümü görün.
        </Typography>
        <Box
          sx={{
            mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'background.neutral',
            display: 'inline-flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center',
          }}
        >
          <Iconify icon="solar:info-circle-bold" width={18} sx={{ color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{SCORE_FORMULA_TR}</Typography>
        </Box>
      </Box>

      <Card sx={{ p: 3, mb: 3 }}>
        <ReportScopeFilter />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small" type="date" label="Başlangıç" InputLabelProps={{ shrink: true }}
            value={start} onChange={(e) => setStart(e.target.value)}
          />
          <TextField
            size="small" type="date" label="Bitiş" InputLabelProps={{ shrink: true }}
            value={end} onChange={(e) => setEnd(e.target.value)}
          />
          <Button variant="contained" onClick={handleFetch} disabled={loading || rangeInvalid}>
            {loading ? 'Yükleniyor…' : 'Raporu Getir'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined" color="error" startIcon={<Iconify icon="solar:file-text-bold" />}
            disabled={!data || exporting !== null} onClick={() => handleExport('PDF')}
          >
            {exporting === 'PDF' ? 'PDF…' : 'PDF'}
          </Button>
          <Button
            variant="outlined" color="success" startIcon={<Iconify icon="solar:file-download-bold" />}
            disabled={!data || exporting !== null} onClick={() => handleExport('EXCEL')}
          >
            {exporting === 'EXCEL' ? 'Excel…' : 'Excel'}
          </Button>
        </Stack>
        {error && <Typography variant="body2" sx={{ color: 'error.main', mt: 2 }}>{error}</Typography>}
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data && !loading && rows.length > 0 && (
        <Card sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
            <Box>
              <Typography variant="h6">{rows.length}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Çalışan</Typography>
            </Box>
            <Box>
              <Typography variant="h6">{avgScore}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ortalama skor</Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {DIST_META.map((m) => (
                <Chip
                  key={m.key}
                  color={m.color}
                  variant={dist[m.key] ? 'filled' : 'outlined'}
                  label={`${m.key}: ${dist[m.key]}`}
                />
              ))}
            </Stack>
          </Stack>
        </Card>
      )}

      {data && !loading && (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell sx={{ width: 48 }}>#</TableCell>
                  <TableCell>Çalışan</TableCell>
                  <TableCell>Şube</TableCell>
                  <TableCell align="center">Geç</TableCell>
                  <TableCell align="center">Erken</TableCell>
                  <TableCell align="center">Devamsız</TableCell>
                  <TableCell align="center">Unutulan</TableCell>
                  <TableCell align="center">İş Günü</TableCell>
                  <TableCell align="center">Skor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((emp, idx) => {
                  const open = expanded.has(emp.employeeId);
                  return (
                    <Fragment key={emp.employeeId}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(emp.employeeId)}>
                        <TableCell>
                          <IconButton size="small">
                            <Iconify icon={open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'} />
                          </IconButton>
                        </TableCell>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{emp.fullName}</TableCell>
                        <TableCell>{emp.branchName}</TableCell>
                        <TableCell align="center">{emp.lateCount}</TableCell>
                        <TableCell align="center">{emp.earlyLeaveCount}</TableCell>
                        <TableCell align="center">{emp.absenceCount}</TableCell>
                        <TableCell align="center">{emp.forgottenCheckoutCount}</TableCell>
                        <TableCell align="center">{emp.expectedWorkdays}</TableCell>
                        <TableCell align="center">
                          {(() => {
                            const sev = severity(emp.score, emp.expectedWorkdays);
                            return (
                              <Tooltip title={`Risk: ${sev.label} (%${sev.pct}) · ham skor ${emp.score}`} arrow>
                                <Chip size="small" color={sev.color} label={`${emp.score} · ${sev.label}`} />
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                          <Collapse in={open} unmountOnExit>
                            <EventDetail emp={emp} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      Kayıt bulunamadı.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </DashboardContent>
  );
}

export function RegularityRankingView({ mode }: { mode: RegularityMode }) {
  return <RegularityInner mode={mode} />;
}

export function IrregularEmployeesView() {
  return <RegularityRankingView mode="irregular" />;
}

export function RegularEmployeesView() {
  return <RegularityRankingView mode="regular" />;
}
